import { Box, Button, Grid } from "@dehub/theme";
import { i18n } from "@dehub/theme";
import { useForm } from "@dehub/theme";
import dayjs from "dayjs";
import { yupResolver } from "mantine-form-yup-resolver";
import React, { forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";
import { PubGeneralApi } from "@/apis";
import type { IReference } from "@/models/Reference";
import { dateTimeFormat } from "@/utils/date";
import { message } from "@/utils/message";
import { RegisterFormFinanceSection } from "./register-form-finance-section";
import { RegisterFormIdentitySection } from "./register-form-identity-section";
import classes from "./register-form.module.css";

export type IFormData = {
  type: string;
  regNumber: string;
  businessName: string;
  businessNameEng: string;
  firstName: string;
  phone: string;
  email: string;
  dehubFinanceIsManager: boolean;
  dehubFinance: {
    lastName: string;
    firstName: string;
    email: string;
    phone: string;
  };
};

const buildSchema = () =>
  yup.object({
    type: yup.string().required(i18n.t("common:required")),
    businessName: yup
      .string()
      .matches(/^[А-Яа-яӨөҮүЁё\s.-]{2,255}$/, i18n.t("common:validation.cyrillicOnlyShort"))
      .required(i18n.t("common:required")),
    // Optional: XYP holds no Latin name for many companies, and it is never
    // transliterated -- an empty value means the Cyrillic name keeps showing.
    businessNameEng: yup
      .string()
      .matches(/^[A-Za-z0-9\s.,'&()-]{2,255}$/, { message: i18n.t("common:validation.latinOnlyShort"), excludeEmptyString: true })
      .optional(),
    firstName: yup
      .string()
      .matches(/^[А-Яа-яӨөҮүЁё\s.-]{2,100}$/, i18n.t("common:validation.cyrillicOnlyShort"))
      .required(i18n.t("common:required")),
    email: yup.string().email(i18n.t("register:form.emailEnterPlaceholder")).required(i18n.t("common:required")).nullable(),
    phone: yup
      .string()
      .matches(/^[986]\d{7}$/, i18n.t("common:validation.phoneOnly"))
      .required(i18n.t("common:required"))
      .nullable(),
    regNumber: yup
      .string()
      .when("type", {
        is: "CITIZEN",
        then: (schema) => schema.matches(/^[А-Яа-яҮүӨөЁё]{2}\d{8}$/, i18n.t("register:form.citizenRegError")).required(i18n.t("common:required")),
      })
      .when("type", {
        is: "COMPANY",
        then: (schema) => schema.matches(/^\d{7}$/, i18n.t("register:form.companyRegError")).required(i18n.t("common:required")),
      })
      .required(i18n.t("common:required")),
    dehubFinanceIsManager: yup.boolean().optional(),
    dehubFinance: yup.object().when(["dehubFinanceIsManager"], ([isManager]) => {
      if (isManager === false) {
        return yup.object({
          lastName: yup.string().required(i18n.t("register:form.requiredPolite")),
          firstName: yup.string().required(i18n.t("register:form.requiredPolite")),
          email: yup.string().email().required(i18n.t("register:form.requiredPolite")),
          phone: yup
            .string()
            .matches(/^[986]\d{7}$/, i18n.t("common:validation.phoneOnly"))
            .required(i18n.t("common:required")),
        });
      }

      return yup.object({
        lastName: yup.string().notRequired(),
        firstName: yup.string().notRequired(),
        email: yup.string().notRequired(),
        phone: yup.string().notRequired(),
      });
    }),
  });

export type RegisterFormRef = {
  submit: () => Promise<IFormData | null>;
  validate: () => Promise<Record<string, string>>;
  setFormData: (callback: (state: IFormData) => IFormData) => void;
};

type RegisterFormProps = { setAction: (action: any[]) => void; initData: { partnerTypes: IReference[] } };

export const RegisterForm = forwardRef<RegisterFormRef, RegisterFormProps>(({ setAction, initData }, ref) => {
  const { t } = useTranslation("register");
  const { partnerTypes } = initData;
  const [loading, setLoading] = React.useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const quotationRef = searchParams.get("quotationRef");

  const form = useForm<IFormData>({
    initialValues: {
      type: "COMPANY",
      regNumber: "",
      businessName: "",
      businessNameEng: "",
      firstName: "",
      phone: "",
      email: "",
      dehubFinanceIsManager: false,
      dehubFinance: {
        lastName: "",
        firstName: "",
        email: "",
        phone: "",
      },
    },
    validate: yupResolver(buildSchema()),
  });

  const onSubmit = async (values: IFormData) => {
    setLoading(true);

    try {
      const res = await PubGeneralApi.register({
        type: values.type ?? "",
        businessName: values.businessName ?? "",
        businessNameEng: values.businessNameEng ?? "",
        firstName: values.firstName ?? "",
        email: values.email ?? "",
        dehubFinanceIsManager: values.dehubFinanceIsManager,
        dehubFinance:
          values.dehubFinanceIsManager === false
            ? {
                lastName: values.dehubFinance?.lastName ?? "",
                firstName: values.dehubFinance?.firstName ?? "",
                email: values.dehubFinance?.email ?? "",
                phone: values.dehubFinance?.phone ?? "",
              }
            : null,
        phone: values.phone?.toString() ?? "",
        regNumber: `${values.regNumber ?? ""}`,
        ...(quotationRef ? { quotationRef } : {}),
      });
      message.success(t("form.emailSentSuccess", { time: dateTimeFormat(dayjs(new Date()).format()) }));
      setAction(["verify", { ...values, verifyId: res.verifyId }]);
    } catch (error: any) {
      message.error(error.message ?? t("form.actionFailed"));
    }

    setLoading(false);
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const result = form.validate();
      if (result.hasErrors) return null;
      return form.values;
    },
    validate: async () => {
      const result = form.validate();
      const flat: Record<string, string> = {};
      Object.entries(result.errors).forEach(([k, v]) => {
        flat[k] = String(v);
      });
      return flat;
    },
    setFormData: (cb) => {
      form.setValues((current) => cb(current as IFormData));
    },
  }));

  return (
    <form onSubmit={form.onSubmit(onSubmit)} noValidate>
      <Box className={classes.container}>
        <Grid gap={{ base: 12, sm: 16, md: 24, lg: 16 }}>
          <Grid gap="xl">
            <RegisterFormIdentitySection form={form} partnerTypes={partnerTypes} />
            <RegisterFormFinanceSection form={form} />
            <Grid.Col span={12}>
              <Button loading={loading} w="100%" type="submit" color="brand" radius="md" className={classes.btn}>
                {t("form.submit")}
              </Button>
            </Grid.Col>
          </Grid>
        </Grid>
      </Box>
    </form>
  );
});
